import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../../utils/logger';
import { 
  ArrowLeft, 
  Mail, 
  Building, 
  Calendar, 
  Star, 
  Phone, 
  MessageSquare, 
  MousePointer, 
  TrendingUp, 
  Archive, 
  Edit,
  ExternalLink,
  Clock,
  Tag,
  User,
  X,
  Target,
  Send,
  Paperclip,
  Smile,
  Plus,
  CheckSquare,
  CheckCircle,
  FileText,
  Activity,
  Globe,
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Linkedin,
  BarChart3,
  AlertCircle,
  Info,
  Bot
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import ContactManager from '../contacts/ContactManager';
import { LEAD_STATUS, EVENT_TYPES, supabase } from '../../config/supabase';
import GoogleCalendarService from '../../services/googleCalendarService';

const LeadDetail = ({ lead, onBack }) => {
  const { 
    fetchLeadEvents, 
    updateLeadStatus, 
    fetchConversations, 
    fetchMessages,
    sendMessage,
    sendEmailReply, 
    fetchTasks, 
    addTask, 
    toggleTask, 
    fetchNotes, 
    addNote, 
    fetchMeetings, 
    scheduleMeeting,
    conversations,
    messages,
    notes,
    tasks,
    meetings,
    userTimezone,
    formatDateInUserTimezone,
    formatTimeInUserTimezone
  } = useData();
  
  // State variables
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rightPanelView, setRightPanelView] = useState('timeline');
  const [messageText, setMessageText] = useState('');
  const [newTask, setNewTask] = useState('');
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: 30,
    type: 'call',
    syncToGoogle: true
  });
  const [leadNotes, setLeadNotes] = useState([]);
  const [leadTasks, setLeadTasks] = useState([]);
  const [leadMeetings, setLeadMeetings] = useState([]);
  const [leadConversations, setLeadConversations] = useState([]);
  const [leadMessages, setLeadMessages] = useState([]);
  
  // Filter messages for current lead from global state
  const currentLeadMessages = messages.filter(msg => msg.lead_email === lead?.email);
  const [timelineEvents, setTimelineEvents] = useState([]);
  
  // Email reply state
  const [messageType, setMessageType] = useState('message'); // 'message' or 'email'
  const [isEmailReply, setIsEmailReply] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [replyToMessageId, setReplyToMessageId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  
  // Resizable panel state
  const [leftWidth, setLeftWidth] = useState(320);
  const [rightWidth, setRightWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(null);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  
  const actualLeftWidth = leftCollapsed ? 0 : leftWidth;
  const actualRightWidth = rightCollapsed ? 0 : rightWidth;
  
  // Activity Tracking Popup state
  const [showActivityTracking, setShowActivityTracking] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [showScoringGuide, setShowScoringGuide] = useState(false);
  const [showColorHint, setShowColorHint] = useState(false);
  
  // Resize handlers
  const handleMouseDown = (side) => (e) => {
    setIsResizing(side);
    document.body.style.cursor = 'col-resize';
  };
  
  const handleMouseMove = (e) => {
    if (!isResizing) return;
    
    const container = document.querySelector('[data-resize-container]');
    if (!container) return;
    
    const containerRect = container.getBoundingClientRect();
    const mouseX = e.clientX - containerRect.left;
    
    if (isResizing === 'left') {
      const newWidth = Math.max(200, Math.min(500, mouseX));
      setLeftWidth(newWidth);
    } else if (isResizing === 'right') {
      const newWidth = Math.max(200, Math.min(400, containerRect.width - mouseX));
      setRightWidth(newWidth);
    }
  };
  
  const handleMouseUp = () => {
    setIsResizing(null);
    document.body.style.cursor = 'default';
  };
  
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  // Fetch lead-specific data when component mounts or lead changes
  useEffect(() => {
    if (lead?.email) {
      loadLeadData();
    }
  }, [lead?.email]);

  const loadLeadData = async () => {
    if (!lead?.email) return;
    
    try {
      // Fetch all lead-related data
      const [notesData, tasksData, meetingsData, conversationsData, messagesData, eventsData] = await Promise.all([
        fetchNotes(lead.email),
        fetchTasks(lead.email),
        fetchMeetings(lead.email),
        fetchConversations(lead.email),
        fetchMessages(null, lead.email), // This updates global messages state
        fetchLeadEvents(lead.email)
      ]);
      
      setLeadNotes(notesData);
      setLeadTasks(tasksData);
      setLeadMeetings(meetingsData);
      setLeadConversations(conversationsData);
      // No need to set leadMessages since we're using currentLeadMessages from global state
      
      // Process email events from leadEvents (both sent and received)
      const emailEvents = eventsData
        .filter(event => (event.event_type === 'reply' && event.reply_text) || (event.event_type === 'sent' && event.email_content))
        .map(event => {
          // Determine if this is a sent or received email
          const isSentEmail = event.event_type === 'sent';
          const emailText = isSentEmail ? event.email_content : event.reply_text;
          
          // Extract clean message text (same logic as inbox)
          let messageText = emailText.trim();
          
          // Remove email headers and quoted content
          const lines = messageText.split('\n');
          const cleanLines = [];
          
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            
            // Stop at common email reply indicators
            if (line.startsWith('On ') && (line.includes(' wrote:') || (line.includes(' at ') && line.includes('>')))) {
              break;
            }
            
            // Skip lines with timestamp and sender info
            if (line.includes(' wrote:') && (line.includes('AM ') || line.includes('PM ') || line.includes('@'))) {
              continue;
            }
            
            // Skip "On [date] at [time]" patterns
            if (/On\s+\w+,\s+\w+\s+\d+,\s+\d+\s+at\s+\d+:\d+\s*[AP]M/.test(line)) {
              continue;
            }
            
            // Skip lines with sender names and email patterns
            if (line.includes('<') && (line.includes('@') || /^[A-Z\s]+</.test(line))) {
              continue;
            }
            
            // Skip quoted lines that start with >
            if (line.startsWith('>')) {
              continue;
            }
            
            // Skip email headers
            if (line.includes('@') && (line.includes('<') || line.includes('From:') || line.includes('To:'))) {
              continue;
            }
            
            // Skip lines that are just sender names or email signatures
            if (/^[A-Z\s]+$/.test(line) && line.length > 10) {
              continue;
            }
            
            if (line.length > 0) {
              cleanLines.push(line);
            }
          }
          
          messageText = cleanLines.join('\n').trim();
          
          return {
            id: event.id,
            content: messageText,
            created_at: event.created_at,
            type: 'email_event',
            message_type: isSentEmail ? 'sent' : 'received',
            is_email: true
          };
        })
        .filter(email => email.content && email.content.length > 0);
      
      // Add email events to conversations
      setLeadConversations(prev => [...prev, ...emailEvents]);
      
      // Create timeline events from all activities
      const timeline = [];
      
      // Add notes to timeline
      notesData.forEach(note => {
        timeline.push({
          id: `note-${note.id}`,
          type: 'note',
          title: 'Note Added',
          description: note.content,
          timestamp: note.created_at,
          icon: FileText
        });
      });
      
      // Add tasks to timeline
      tasksData.forEach(task => {
        timeline.push({
          id: `task-${task.id}`,
          type: 'task',
          title: task.completed ? 'Task Completed' : 'Task Created',
          description: task.title,
          timestamp: task.completed ? task.updated_at : task.created_at,
          icon: CheckSquare
        });
      });
      
      // Add meetings to timeline
      meetingsData.forEach(meeting => {
        timeline.push({
          id: `meeting-${meeting.id}`,
          type: 'meeting',
          title: 'Meeting Scheduled',
          description: meeting.title,
          timestamp: meeting.created_at,
          icon: Calendar
        });
      });
      
      // Add messages to timeline
      messagesData.forEach(message => {
        timeline.push({
          id: `message-${message.id}`,
          type: 'message',
          title: message.message_type === 'sent' ? 'Message Sent' : 'Message Received',
          description: message.content.substring(0, 100) + (message.content.length > 100 ? '...' : ''),
          timestamp: message.created_at,
          icon: MessageSquare
        });
      });
      
      // Sort timeline by timestamp (newest first)
      timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setTimelineEvents(timeline);
      
    } catch (error) {
      logger.error('Error loading lead data:', error);
    }
  };
  
  const [contactData, setContactData] = useState({
    firstName: lead?.first_name || '',
    lastName: lead?.last_name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    jobTitle: lead?.job_title || '',
    linkedin: lead?.linkedin || '',
    company: lead?.company || '',
    industry: lead?.industry || '',
    companySize: lead?.company_size || '',
    website: lead?.website || '',
    location: lead?.location || '',
    annualRevenue: lead?.annual_revenue || '',
    tags: lead?.tags || ['Lead', 'Prospect']
  });
  
  const rightPanelViews = [
    { id: 'timeline', name: 'Timeline', icon: Clock },
    { id: 'notes', name: 'Notes', icon: FileText },
    { id: 'tasks', name: 'Tasks', icon: CheckSquare },
    { id: 'calendar', name: 'Calendar', icon: Calendar }
  ];
  
  // Event handlers
  const handleSendConversationMessage = async () => {
    if (!newMessage.trim() || !lead?.email || isSending) return;
    
    setIsSending(true);
    
    try {
      if (messageType === 'email') {
        // Send email reply via n8n and Instantly
        await sendEmailReply(lead.email, newMessage, replyToMessageId);
        logger.log('✓ Email reply sent successfully');
      } else {
        // Send regular internal message
        await sendMessage(lead.email, newMessage, 'sent');
        logger.log('✓ Internal message sent successfully');
      }
      
      setNewMessage('');
      setIsEmailReply(false);
      setMessageType('message');
      setReplyToMessageId(null);
      
      // Messages will be automatically updated via real-time subscription
    } catch (error) {
      logger.error('Error sending message:', error);
      // Show user-friendly error message
      alert(`Failed to send ${messageType === 'email' ? 'email reply' : 'message'}: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSending(false);
    }
  };
  
  const handleEmailReply = (messageId = null) => {
    setMessageType('email');
    setIsEmailReply(true);
    setReplyToMessageId(messageId);
    setEmailSubject(lead?.reply_subject || `Re: ${lead?.campaign_name}`);
  };
  
  const handleCancelEmailReply = () => {
    setMessageType('message');
    setIsEmailReply(false);
    setReplyToMessageId(null);
    setNewMessage('');
  };
  
  const handleAddTask = async () => {
    if (!newTask.trim() || !lead?.email) return;
    
    try {
      await addTask(lead.email, {
        title: newTask,
        description: '',
        due_date: null,
        priority: 'medium'
      });
      setNewTask('');
      setShowTaskInput(false);
      // Reload tasks
      const updatedTasks = await fetchTasks(lead.email);
      setLeadTasks(updatedTasks);
    } catch (error) {
      logger.error('Error adding task:', error);
    }
  };
  
  const handleAddNote = async () => {
    if (!newNote.trim() || !lead?.email) return;
    
    try {
      await addNote(lead.email, {
        content: newNote,
        note_type: 'general'
      });
      setNewNote('');
      setShowNoteInput(false);
      // Reload notes
      const updatedNotes = await fetchNotes(lead.email);
      setLeadNotes(updatedNotes);
    } catch (error) {
      logger.error('Error adding note:', error);
    }
  };
  
  const handleScheduleMeeting = async () => {
    if (!meetingForm.title.trim() || !meetingForm.date || !meetingForm.time || !lead?.email) return;
    
    try {
      logger.log('=== MEETING FORM DEBUG ===');
      logger.log('Original meetingForm data:', meetingForm);
      logger.log('Lead email:', lead.email);
      logger.log('Meeting date:', meetingForm.date);
      logger.log('Meeting time:', meetingForm.time);
      logger.log('Meeting type:', meetingForm.type);
      logger.log('========================');
      
      await scheduleMeeting(lead.email, meetingForm);
      setMeetingForm({
        title: '',
        description: '',
        date: '',
        time: '',
        duration: 30,
        type: 'call',
        syncToGoogle: true
      });
      setShowMeetingForm(false);
      // Reload meetings
      const updatedMeetings = await fetchMeetings(lead.email);
      setLeadMeetings(updatedMeetings);
    } catch (error) {
      logger.error('Error scheduling meeting:', error);
    }
  };
  
  const handleToggleTask = async (taskId, completed) => {
    try {
      await toggleTask(taskId, completed);
      // Reload tasks
      const updatedTasks = await fetchTasks(lead.email);
      setLeadTasks(updatedTasks);
    } catch (error) {
      logger.error('Error toggling task:', error);
    }
  };
  
  // Load Activity Tracking Data (for popup only)
  const loadActivityTracking = async () => {
    if (!lead?.email) return;
    
    setLoadingActivity(true);
    
    try {
      // Fetch all required data with 900 record limit
      // Include messages table to get REAL reply count from Inbox
      const [eventsData, sequencesData, executionsData, automationOpensData, messagesData] = await Promise.all([
        fetchLeadEvents(lead.email),
        supabase
          .from('email_automation_sequences')
          .select('*')
          .eq('lead_email', lead.email)
          .order('created_at', { ascending: false })
          .limit(900),
        supabase
          .from('email_automation_executions')
          .select('*')
          .eq('lead_email', lead.email)
          .order('created_at', { ascending: false })
          .limit(900),
        supabase
          .from('email_automation_opens_dashboard')
          .select('*')
          .eq('lead_email', lead.email),
        supabase
          .from('messages')
          .select('*')
          .eq('lead_email', lead.email)
          .order('created_at', { ascending: false })
      ]);
      
      const sequences = sequencesData.data || [];
      const executions = executionsData.data || [];
      const allEvents = eventsData || [];
      const inboxMessages = messagesData.data || [];
      
      // Separate event types
      const opens = allEvents.filter(e => e.event_type === 'open');
      
      // Count replies from lead_events table - DEDUPLICATED to match Inbox display
      // Inbox deduplicates messages based on content + timestamp (within 1 second)
      const allReplies = allEvents.filter(e => e.event_type === 'reply' && e.reply_text && e.reply_text.trim().length > 0);
      
      // Deduplicate replies (same content within 1 second = same reply)
      const uniqueReplies = [];
      allReplies.forEach(reply => {
        const content = reply.reply_text.trim();
        const timestamp = new Date(reply.created_at).getTime();
        
        // Check if this is a duplicate
        let isDuplicate = false;
        for (const existing of uniqueReplies) {
          const existingContent = existing.reply_text.trim();
          const existingTime = new Date(existing.created_at).getTime();
          
          // Same content within 1 second = duplicate
          if (existingContent === content && Math.abs(timestamp - existingTime) < 1000) {
            isDuplicate = true;
            break;
          }
        }
        
        if (!isDuplicate) {
          uniqueReplies.push(reply);
        }
      });
      
      const replies = uniqueReplies;
      
      const clicks = allEvents.filter(e => e.event_type === 'click');
      const sends = allEvents.filter(e => e.event_type === 'sent' || e.event_type === 'email_sent');
      
      // Find active sequence
      const activeSequence = sequences.find(s => s.status === 'active');
      const sentExecutions = executions.filter(e => e.status === 'sent');
      
      // Determine if lead has entered automation (has sequences or executions)
      const hasEnteredAutomation = sequences.length > 0 || sentExecutions.length > 0;
      
      // CRITICAL: Correct classification of opens
      // - Automation Opens: ONLY from email_automation_opens_dashboard (pixel tracking via SMTP)
      // - Email Opens: ALL opens from lead_events (includes Instantly opens)
      // Instantly opens should NOT count as automation opens.
      
      // Automation Opens = ONLY from pixel tracking (email_automation_opens_dashboard)
      const emailOpensFromDashboard = automationOpensData.data || [];
      const automationOpensCount = hasEnteredAutomation 
        ? emailOpensFromDashboard.reduce((sum, row) => sum + (row.open_count || 1), 0)
        : 0;
      
      // Email Opens = ALL opens from lead_events (Instantly + pixel combined, for general display)
      let emailOpensCount = opens.length;
      
      // Universal rule: if automation opens exist, email opens should be at least 1
      if (automationOpensCount > 0 && emailOpensCount === 0) {
        emailOpensCount = 1;
      }
      
      // Total Opens = ALL opens from lead_events (this is what Activity Summary shows)
      const totalOpensCount = opens.length;
      
      // Calculate real current step based on follow-up sequence logic
      // Current step = next step to be sent (pending step)
      let realCurrentStep = activeSequence ? activeSequence.current_step : 0;
      
      // NEW RULE: If lead has 0 opens, they never entered automation
      // Display as 0/5
      if (opens.length === 0) {
        realCurrentStep = 0;
      } else if (sentExecutions.length > 0) {
        // Fix: Calculate the NEXT pending step based on what's been sent
        let highestStepSent = 0;
        sentExecutions.forEach(exec => {
          if (exec.sequence_step > highestStepSent) {
            highestStepSent = exec.sequence_step;
          }
        });
        
        // Current step = next step after highest sent
        // If step 2 is sent, current step is 3
        realCurrentStep = Math.min(highestStepSent + 1, 5);
      } else {
        // No steps sent yet, current step is 1
        realCurrentStep = 1;
      }
      
      // Build step data (1-5) - use pixel tracking data for open counts per step
      const stepDetails = [];
      for (let step = 1; step <= 5; step++) {
        const stepSents = sentExecutions.filter(e => e.sequence_step === step);
        // Use pixel-tracked opens (email_automation_opens_dashboard) for per-step open counts
        // NOT lead_events (which includes Instantly opens)
        const stepPixelOpens = emailOpensFromDashboard.filter(o => o.sequence_step === step);
        const stepOpenCount = stepPixelOpens.reduce((sum, row) => sum + (row.open_count || 1), 0);
        
        const hasOpens = stepOpenCount > 0;
        const hasSent = stepSents.length > 0;
        
        let status = 'Pending';
        if (hasOpens) {
          status = 'Opened';
        } else if (hasSent) {
          status = 'Sent';
        }
        
        stepDetails.push({
          step,
          sentCount: stepSents.length,
          openCount: stepOpenCount,
          hasOpens,
          hasSent,
          status,
          isCurrent: step === realCurrentStep,
          isPrevious: step < realCurrentStep
        });
      }
      
      // Build activity summary table data
      const activitySummary = [];
      
      // Add all events with proper categorization
      allEvents.forEach(event => {
        let type = event.event_type;
        const hasReplyText = !!(event.reply_text && event.reply_text.trim().length > 0);
        
        // Use score_delta from event (actual stored score from DB)
        let score = event.score_delta || 0;
        
        // Format event type for display
        // All opens shown as 'Open' - no distinction between email/automation opens in activity list
        if (type === 'open') {
          type = 'Open';
        } else if (type === 'reply') {
          if (hasReplyText) {
            type = 'Reply';
          } else {
            type = 'Activity';
            score = 0; // Activity events (reply without reply_text) should not show any score
          }
        } else if (type === 'sent' || type === 'email_sent') {
          type = 'Sent';
        } else if (type === 'click') {
          type = 'Click';
        }
        
        activitySummary.push({
          type,
          campaign: event.campaign_name || 'Manual',
          step: event.step || '-',
          score: score > 0 ? `+${score}` : score,
          timestamp: event.created_at
        });
      });
      
      // Sort by timestamp (newest first)
      activitySummary.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      // Calculate summary statistics
      const totalSends = sends.length;
      const automationSends = sentExecutions.length;
      const totalReplies = replies.length;
      
      // Use lead's actual score from database (not calculated)
      // This is the real-time lead score that updates based on scoring events
      const leadScore = lead.score || 0;
      
      // hasEnteredAutomation is already calculated above - used for conditional Step Progress display
      
      setActivityData({
        summary: {
          totalSends,
          automationSends,
          totalOpens: totalOpensCount,
          automationOpens: automationOpensCount,
          emailOpens: emailOpensCount,
          totalReplies,
          currentStep: realCurrentStep,
          maxSteps: activeSequence ? activeSequence.max_steps : 5,
          leadScore,
          hasEnteredAutomation
        },
        stepProgress: stepDetails,
        activityTable: activitySummary,
        rawData: {
          sequences,
          executions,
          allEvents
        }
      });
      
    } catch (error) {
      logger.error('Error loading activity tracking:', error);
    } finally {
      setLoadingActivity(false);
    }
  };
  
  if (!lead) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No lead selected</h3>
          <p className="text-gray-500">Select a lead to view details</p>
        </div>
      </div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-gray-50 flex flex-col h-full max-h-screen"
      style={{ overflow: 'hidden' }}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </motion.button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{lead?.email}</h1>
              <p className="text-sm text-gray-500">
                {lead?.first_name && `${lead.first_name} • `}
                {lead?.company || 'Lead Details'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setShowActivityTracking(true);
                loadActivityTracking();
              }}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center space-x-2"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Activity Tracking</span>
            </motion.button>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              lead?.score >= 25 ? 'bg-red-100 text-red-800' :
              lead?.score >= 20 ? 'bg-orange-100 text-orange-800' :
              lead?.score >= 10 ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              <Star className="w-4 h-4 inline mr-1" />
              Score: {lead?.score || 0}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Layout */}
      <div className="flex-1 flex min-h-0 overflow-hidden" data-resize-container>
        {/* Left Column - Contact Manager */}
        {!leftCollapsed && (
          <div 
            className="bg-white border-r border-gray-200 flex-shrink-0 transition-all duration-300 flex flex-col"
            style={{ width: `${actualLeftWidth}px` }}
          >
            <div className="flex-1 overflow-y-auto">
              <ContactManager 
                contact={lead} 
                onUpdate={(updatedData) => {
                  // Handle contact update
                }}
              />
            </div>
          </div>
        )}
        
        {/* Left Resize Handle */}
        <div className="relative flex-shrink-0">
          <div 
            className="w-1 bg-gray-200 hover:bg-blue-300 cursor-col-resize transition-colors h-full"
            onMouseDown={handleMouseDown('left')}
          />
          <button
            onClick={() => setLeftCollapsed(!leftCollapsed)}
            className="absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-8 bg-white border border-gray-300 rounded-r-md shadow-sm hover:bg-gray-50 flex items-center justify-center transition-colors"
            title={leftCollapsed ? 'Expand Contact Panel' : 'Collapse Contact Panel'}
          >
            {leftCollapsed ? (
              <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>
        
        {/* Middle Column - Conversation */}
        <div className="flex-1 bg-white flex flex-col min-w-0 overflow-hidden" style={{ minWidth: '400px' }}>
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {(currentLeadMessages.length > 0 || leadConversations.length > 0) ? (
              // Combine and deduplicate messages to prevent showing duplicates
              (() => {
                const allMessages = [...currentLeadMessages, ...leadConversations.filter(conv => conv.type === 'email_event')];
                const uniqueMessages = [];
                const seenMessages = new Set();
                
                allMessages.forEach(current => {
                  // Create multiple possible keys to catch duplicates with different property names
                  const content = current.content || current.message || '';
                  const timestamp = current.created_at;
                  const messageType = current.message_type || current.sender_type || '';
                  
                  // Create a normalized key
                  const normalizedKey = `${content.trim()}-${timestamp}-${messageType}`;
                  
                  // Also check for near-duplicate timestamps (within 1 second) with same content
                  const contentKey = content.trim();
                  const currentTime = new Date(timestamp).getTime();
                  
                  let isDuplicate = false;
                  
                  // Check exact match first
                  if (seenMessages.has(normalizedKey)) {
                    isDuplicate = true;
                  } else {
                    // Check for near-duplicates (same content within 1 second)
                    for (const existingMsg of uniqueMessages) {
                      const existingContent = (existingMsg.content || existingMsg.message || '').trim();
                      const existingTime = new Date(existingMsg.created_at).getTime();
                      
                      if (existingContent === contentKey && Math.abs(currentTime - existingTime) < 1000) {
                        isDuplicate = true;
                        break;
                      }
                    }
                  }
                  
                  if (!isDuplicate) {
                    seenMessages.add(normalizedKey);
                    uniqueMessages.push(current);
                  }
                });
                
                return uniqueMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
              })()
                .map((item, index) => {
                  const isEmailEvent = item.type === 'email_event';
                  const isMessage = !isEmailEvent;
                  const isSent = item.message_type === 'sent' || item.sender_type === 'agent';
                  const emailStatus = item.email_status || 'sent'; // Default to sent for backward compatibility
                  const isPending = emailStatus === 'pending';
                  const isFailed = emailStatus === 'failed';
                  
                  return (
                    <motion.div
                      key={isEmailEvent ? `email-${item.id}-${item.created_at}` : `msg-${item.id}-${item.created_at || index}`}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`flex ${
                        isSent ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div className={`max-w-xs lg:max-w-md px-3 py-2 rounded-2xl shadow-sm relative ${
                        isEmailEvent && isSent
                          ? isPending
                            ? 'bg-yellow-500 text-white rounded-br-md'
                            : isFailed
                            ? 'bg-red-500 text-white rounded-br-md'
                            : 'bg-blue-600 text-white rounded-br-md'
                          : isEmailEvent && !isSent
                          ? item.validation_status === 'valid'
                            ? 'bg-white text-gray-900 rounded-bl-md border-2 border-green-400'
                            : item.validation_status === 'auto_reply' || item.validation_status === 'out_of_office'
                            ? 'bg-yellow-50 text-gray-900 rounded-bl-md border-2 border-yellow-400'
                            : 'bg-green-50 text-green-900 rounded-bl-md border border-green-200'
                          : isSent
                          ? isPending
                            ? 'bg-yellow-500 text-white rounded-br-md'
                            : isFailed
                            ? 'bg-red-500 text-white rounded-br-md'
                            : 'bg-blue-600 text-white rounded-br-md'
                          : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                      }`}>
                        {/* Validation Status Indicator for received messages */}
                        {!isSent && item.validation_status && (
                          <div className={`absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                            item.validation_status === 'valid' 
                              ? 'bg-green-500' 
                              : item.validation_status === 'auto_reply' || item.validation_status === 'out_of_office'
                              ? 'bg-yellow-500'
                              : 'bg-gray-400'
                          }`}>
                            {item.validation_status === 'valid' ? (
                              <CheckCircle className="w-3 h-3 text-white" />
                            ) : item.validation_status === 'auto_reply' || item.validation_status === 'out_of_office' ? (
                              <Bot className="w-3 h-3 text-white" />
                            ) : (
                              <AlertCircle className="w-3 h-3 text-white" />
                            )}
                          </div>
                        )}
                        {/* Status indicator for pending/failed messages */}
                        {isSent && (isPending || isFailed) && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center">
                            {isPending && (
                              <div className="w-2 h-2 bg-yellow-300 rounded-full animate-pulse"></div>
                            )}
                            {isFailed && (
                              <div className="w-2 h-2 bg-red-300 rounded-full"></div>
                            )}
                          </div>
                        )}
                        
                        <p className="text-sm leading-relaxed">{item.content || item.message}</p>
                        
                        {/* Validation Status Label for received messages */}
                        {!isSent && item.validation_status && item.validation_status !== 'pending' && (
                          <div className={`text-xs mt-1 px-2 py-0.5 rounded-full inline-block ${
                            item.validation_status === 'valid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.validation_status === 'valid' ? 'Valid Reply' : 
                             item.validation_status === 'auto_reply' ? 'Auto Reply' : 'Out of Office'}
                          </div>
                        )}
                        
                        {/* Error message for failed emails */}
                        {isFailed && item.error_message && (
                          <div className="mt-2 p-2 bg-red-600 bg-opacity-20 rounded text-xs">
                            <span className="font-medium">Error: </span>
                            {item.error_message}
                          </div>
                        )}
                        
                        <div className={`flex items-center justify-between mt-1 ${
                           isEmailEvent && isSent ? 'text-blue-100' :
                           isEmailEvent && !isSent ? 'text-green-600' :
                           isSent ? 'text-blue-100' : 'text-gray-400'
                         }`}>
                           <span className="text-xs">
                             {new Date(item.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                           </span>
                           
                           {isSent && (
                             <div className="flex items-center space-x-1">
                               {isPending && (
                                 <div className="flex space-x-0.5">
                                   <div className="w-1 h-1 bg-current rounded-full animate-pulse"></div>
                                   <div className="w-1 h-1 bg-current rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                                   <div className="w-1 h-1 bg-current rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                                 </div>
                               )}
                               {isFailed && (
                                 <div className="text-xs">⚠️</div>
                               )}
                               {!isPending && !isFailed && (
                                 <div className="flex space-x-0.5">
                                   <div className="w-1 h-1 bg-current rounded-full opacity-60"></div>
                                   <div className="w-1 h-1 bg-current rounded-full"></div>
                                 </div>
                               )}
                             </div>
                           )}
                         </div>
                      </div>
                    </motion.div>
                  );
                })
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No messages yet</h3>
                  <p className="text-xs text-gray-500">Start a conversation with this lead</p>
                </div>
              </div>
            )}
            <div className="h-4"></div>
          </div>
          
          {/* Message Input */}
          <div className="px-4 py-3 border-t border-gray-100 bg-white">
            {/* Message Type Toggle */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setMessageType('message')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                    messageType === 'message'
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <MessageSquare className="w-3 h-3 mr-1 inline" />
                  Internal Message
                </button>
                <button
                  onClick={() => handleEmailReply()}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                    messageType === 'email'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Mail className="w-3 h-3 mr-1 inline" />
                  Email Reply
                </button>
              </div>
              {isEmailReply && (
                <button
                  onClick={handleCancelEmailReply}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Email Subject (shown when email mode) */}
            {messageType === 'email' && (
              <div className="mb-3">
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Subject:</span>
                    <span className="text-sm text-green-700">{emailSubject}</span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    This will be sent as an email via Instantly
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-end space-x-3">
              <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition-all duration-200 group">
                <Paperclip className="w-4 h-4 group-hover:text-blue-600" />
              </button>
              <div className="flex-1 relative">
                <div className={`border rounded-2xl px-3 py-2 min-h-[40px] flex items-center transition-all duration-200 ${
                  messageType === 'email'
                    ? 'bg-green-50 border-green-200 focus-within:bg-white focus-within:border-green-500 focus-within:ring-2 focus-within:ring-green-100'
                    : 'bg-gray-50 border-gray-200 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100'
                }`}>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={messageType === 'email' ? 'Type your email reply...' : 'Type your message...'}
                    className="flex-1 bg-transparent resize-none outline-none text-sm placeholder-gray-500 max-h-20"
                    rows={1}
                    style={{ minHeight: '20px' }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !isSending) {
                        e.preventDefault();
                        handleSendConversationMessage();
                      }
                    }}
                    disabled={isSending}
                  />
                </div>
              </div>
              <button
                onClick={handleSendConversationMessage}
                disabled={!newMessage.trim() || isSending}
                className={`p-2 rounded-xl transition-all duration-200 ${
                  newMessage.trim() && !isSending
                    ? messageType === 'email'
                      ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSending ? (
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  messageType === 'email' ? <Mail className="w-4 h-4" /> : <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Right Resize Handle */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setRightCollapsed(!rightCollapsed)}
            className="absolute top-1/2 -translate-y-1/2 -left-3 w-6 h-8 bg-white border border-gray-300 rounded-l-md shadow-sm hover:bg-gray-50 flex items-center justify-center transition-colors z-10"
            title={rightCollapsed ? 'Expand Tools Panel' : 'Collapse Tools Panel'}
          >
            {rightCollapsed ? (
              <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            ) : (
              <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
          <div 
            className="w-1 bg-gray-200 hover:bg-blue-300 cursor-col-resize transition-colors h-full"
            onMouseDown={handleMouseDown('right')}
          />
        </div>
        
        {/* Right Column - Tools Panel */}
        {!rightCollapsed && (
          <div 
            className="bg-white border-l border-gray-200 flex flex-col flex-shrink-0 transition-all duration-300 overflow-hidden"
            style={{ width: `${actualRightWidth}px` }}
          >
            {/* Tab Navigation */}
            <div className="border-b border-gray-100 bg-gray-50">
              <nav className="flex p-1" role="tablist">
                {rightPanelViews.map((view) => {
                  const Icon = view.icon;
                  const isActive = rightPanelView === view.id;
                  return (
                    <button
                      key={view.id}
                      onClick={() => setRightPanelView(view.id)}
                      className={`flex-1 flex items-center justify-center py-2 px-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                      role="tab"
                      aria-selected={isActive}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden lg:inline text-xs ml-1 font-semibold">{view.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
            
            {/* Tab Content */}
            <AnimatePresence mode="wait">
              {rightPanelView === 'timeline' && (
                <motion.div
                  key="timeline"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex-1 flex flex-col"
                >
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Activity Timeline</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{timelineEvents.length} activities</p>
                  </div>
                  <div className="flex-1 overflow-y-auto overflow-x-auto">
                    {timelineEvents.length > 0 ? (
                      <div className="p-3 space-y-3 min-w-max">
                        {timelineEvents.map((event, index) => {
                          const IconComponent = event.icon;
                          const isLast = index === timelineEvents.length - 1;
                          return (
                            <div key={event.id} className="relative">
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 relative">
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center shadow-sm">
                                    <IconComponent className="w-4 h-4 text-blue-600" />
                                  </div>
                                  {!isLast && (
                                    <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-0.5 h-6 bg-gray-200"></div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 pb-4">
                                  <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                                    <p className="text-xs font-semibold text-gray-900">{event.title}</p>
                                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                                      {(() => {
                                        try {
                                          const parsed = JSON.parse(event.description);
                                          return parsed.content || event.description;
                                        } catch {
                                          return event.description;
                                        }
                                      })()}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-2 flex items-center">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {new Date(event.timestamp).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <h4 className="text-sm font-medium text-gray-900 mb-1">No activity yet</h4>
                          <p className="text-xs text-gray-500">Activity will appear here as you interact with this lead</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
              
              {rightPanelView === 'notes' && (
                <motion.div
                  key="notes"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex-1 flex flex-col"
                >
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{leadNotes.length} notes</p>
                      </div>
                      <button
                        onClick={() => setShowNoteInput(!showNoteInput)}
                        className={`p-2 rounded-xl transition-all duration-200 ${
                          showNoteInput 
                            ? 'bg-blue-100 text-blue-600' 
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {showNoteInput && (
                      <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-3">
                        <textarea
                          placeholder="Add a note..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white"
                          rows={3}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={handleAddNote}
                            disabled={!newNote.trim()}
                            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-xs font-medium"
                          >
                            Add Note
                          </button>
                          <button
                            onClick={() => {
                              setShowNoteInput(false);
                              setNewNote('');
                            }}
                            className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-300 transition-all duration-200 text-xs font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 overflow-y-auto">
                    {leadNotes.length > 0 ? (
                      <div className="p-3 space-y-3">
                        {leadNotes.map((note) => (
                          <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                            <div className="flex items-start space-x-2">
                              <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <FileText className="w-3 h-3 text-yellow-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-800 leading-relaxed">
                                  {(() => {
                                    try {
                                      const parsed = JSON.parse(note.content);
                                      return parsed.content || note.content;
                                    } catch {
                                      return note.content;
                                    }
                                  })()}
                                </p>
                                <p className="text-xs text-gray-400 mt-2 flex items-center">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {new Date(note.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <h4 className="text-sm font-medium text-gray-900 mb-1">No notes yet</h4>
                          <p className="text-xs text-gray-500">Add notes to keep track of important information</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
              
              {rightPanelView === 'tasks' && (
                <motion.div
                  key="tasks"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex-1 flex flex-col"
                >
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Tasks</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {leadTasks.filter(t => !t.completed).length} pending, {leadTasks.filter(t => t.completed).length} completed
                        </p>
                      </div>
                      <button
                        onClick={() => setShowTaskInput(!showTaskInput)}
                        className={`p-2 rounded-xl transition-all duration-200 ${
                          showTaskInput 
                            ? 'bg-blue-100 text-blue-600' 
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {showTaskInput && (
                      <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-3">
                        <input
                          type="text"
                          placeholder="Add a task..."
                          value={newTask}
                          onChange={(e) => setNewTask(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddTask();
                            }
                          }}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={handleAddTask}
                            disabled={!newTask.trim()}
                            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-xs font-medium"
                          >
                            Add Task
                          </button>
                          <button
                            onClick={() => {
                              setShowTaskInput(false);
                              setNewTask('');
                            }}
                            className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-300 transition-all duration-200 text-xs font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 overflow-y-auto">
                    {leadTasks.length > 0 ? (
                      <div className="p-3 space-y-2">
                        {leadTasks.map((task) => (
                          <div key={task.id} className={`bg-white border rounded-lg p-3 shadow-sm transition-all duration-200 ${
                            task.completed ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-blue-200'
                          }`}>
                            <div className="flex items-start space-x-3">
                              <button
                                onClick={() => handleToggleTask(task.id, !task.completed)}
                                className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                  task.completed
                                    ? 'bg-green-500 border-green-500 text-white'
                                    : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
                                }`}
                              >
                                {task.completed && (
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-medium ${
                                  task.completed ? 'text-green-700 line-through' : 'text-gray-900'
                                }`}>
                                  {task.title}
                                </p>
                                <p className="text-xs text-gray-400 mt-1 flex items-center">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {new Date(task.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <h4 className="text-sm font-medium text-gray-900 mb-1">No tasks yet</h4>
                          <p className="text-xs text-gray-500">Create tasks to track your follow-ups</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
              
              {rightPanelView === 'calendar' && (
                <motion.div
                  key="calendar"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex-1 flex flex-col"
                >
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Meetings</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {leadMeetings.length} scheduled
                        </p>
                      </div>
                      <button
                        onClick={() => setShowMeetingForm(!showMeetingForm)}
                        className={`p-2 rounded-xl transition-all duration-200 ${
                          showMeetingForm 
                            ? 'bg-blue-100 text-blue-600' 
                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {showMeetingForm && (
                      <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
                        <input
                          type="text"
                          placeholder="Meeting title"
                          value={meetingForm.title}
                          onChange={(e) => setMeetingForm(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        />
                        <textarea
                          placeholder="Description (optional)"
                          value={meetingForm.description}
                          onChange={(e) => setMeetingForm(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white resize-none"
                          rows={2}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="date"
                            value={meetingForm.date}
                            onChange={(e) => {
                              logger.log('Date input changed:', e.target.value);
                              setMeetingForm(prev => ({ ...prev, date: e.target.value }));
                            }}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          />
                          <input
                            type="time"
                            value={meetingForm.time}
                            onChange={(e) => {
                              logger.log('Time input changed:', e.target.value);
                              setMeetingForm(prev => ({ ...prev, time: e.target.value }));
                            }}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <select
                            value={meetingForm.duration}
                            onChange={(e) => setMeetingForm(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          >
                            <option value={15}>15 min</option>
                            <option value={30}>30 min</option>
                            <option value={60}>1 hour</option>
                            <option value={90}>1.5 hours</option>
                            <option value={120}>2 hours</option>
                          </select>
                          <select
                            value={meetingForm.type}
                            onChange={(e) => setMeetingForm(prev => ({ ...prev, type: e.target.value }))}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          >
                            <option value="call">📞 Call</option>
                            <option value="video">📹 Video</option>
                            <option value="in-person">🤝 In-person</option>
                          </select>
                        </div>
                        {GoogleCalendarService.isEnabled() && (
                          <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <input
                              type="checkbox"
                              id="syncToGoogle"
                              checked={meetingForm.syncToGoogle}
                              onChange={(e) => setMeetingForm(prev => ({ ...prev, syncToGoogle: e.target.checked }))}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                            />
                            <label htmlFor="syncToGoogle" className="text-sm text-green-800 font-medium">
                              📅 Sync to Google Calendar
                            </label>
                            <span className="text-xs text-green-600">
                              (Creates event in your Google Calendar)
                            </span>
                          </div>
                        )}
                        <div className="flex space-x-2">
                          <button
                            onClick={handleScheduleMeeting}
                            disabled={!meetingForm.title.trim() || !meetingForm.date || !meetingForm.time}
                            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-xs font-medium"
                          >
                            Schedule Meeting
                          </button>
                          <button
                            onClick={() => {
                              setShowMeetingForm(false);
                              setMeetingForm({
                                title: '',
                                description: '',
                                date: '',
                                time: '',
                                duration: 30,
                                type: 'call',
                                syncToGoogle: true
                              });
                            }}
                            className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-300 transition-all duration-200 text-xs font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 overflow-y-auto">
                    {leadMeetings.length > 0 ? (
                      <div className="p-3 space-y-3">
                        {leadMeetings.map((meeting) => (
                          <div key={meeting.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">{meeting.title}</h4>
                                {meeting.description && (
                                  <p className="text-xs text-gray-600 mb-2">{meeting.description}</p>
                                )}
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                                meeting.meeting_type === 'video' ? 'bg-blue-100 text-blue-800' :
                                meeting.meeting_type === 'call' ? 'bg-green-100 text-green-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                <span>
                                  {meeting.meeting_type === 'video' ? '📹' : meeting.meeting_type === 'call' ? '📞' : '🤝'}
                                </span>
                                <span>{meeting.meeting_type}</span>
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span className="flex items-center bg-gray-50 px-2 py-1 rounded-md">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {formatDateInUserTimezone(meeting.meeting_date, { dateStyle: 'short' })}
                                </span>
                                <span className="flex items-center bg-gray-50 px-2 py-1 rounded-md">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {formatTimeInUserTimezone(meeting.meeting_time, meeting.meeting_date)}
                                </span>
                                {meeting.duration && (
                                  <span className="bg-gray-50 px-2 py-1 rounded-md">
                                    ⏱️ {meeting.duration} min
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {meeting.calendar_sync_status && (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                                    meeting.calendar_sync_status === 'synced' ? 'bg-green-100 text-green-800' :
                meeting.calendar_sync_status === 'syncing' ? 'bg-yellow-100 text-yellow-800' :
                meeting.calendar_sync_status === 'sync_failed' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    <span>
                                      {meeting.calendar_sync_status === 'synced' ? '✅' :
               meeting.calendar_sync_status === 'syncing' ? '⏳' :
               meeting.calendar_sync_status === 'sync_failed' ? '❌' : '📅'}
                                    </span>
                                    <span>{GoogleCalendarService.getSyncStatusDisplay(meeting.calendar_sync_status).text}</span>
                                  </span>
                                )}
                                {meeting.google_calendar_link && (
                                  <a
                                    href={meeting.google_calendar_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                    title="View in Google Calendar"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                                {meeting.video_link && (
                                  <a
                                    href={meeting.video_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-800 transition-colors"
                                    title="Join Video Call"
                                  >
                                    📹
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <h4 className="text-sm font-medium text-gray-900 mb-1">No meetings scheduled</h4>
                          <p className="text-xs text-gray-500">Schedule meetings to track your appointments</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      
      {/* Activity Tracking Popup Modal */}
      <AnimatePresence>
        {showActivityTracking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowActivityTracking(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Activity Tracking</h2>
                  <p className="text-blue-100 text-sm mt-0.5">{lead?.email}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowScoringGuide(!showScoringGuide)}
                    className="px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-xs font-medium rounded-lg transition-all duration-200 flex items-center space-x-1"
                  >
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span>Scoring Guide</span>
                  </button>
                  <button
                    onClick={() => setShowActivityTracking(false)}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-200"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
              
              {/* Modal Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                {loadingActivity ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading activity data...</p>
                    </div>
                  </div>
                ) : activityData ? (
                  <div className="p-6 space-y-6">
                    {/* Scoring Guide Popup */}
                    {showScoringGuide && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4 shadow-sm"
                      >
                        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
                          <TrendingUp className="w-4 h-4 mr-2 text-indigo-600" />
                          Activity Scoring Rules
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white rounded-lg p-3 border border-indigo-100">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700">Email Open</span>
                              <span className="text-sm font-bold text-green-600">+5 points</span>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-100">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700">Valid Email Reply</span>
                              <span className="text-sm font-bold text-purple-600">+25 points</span>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-100">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700">Email Sent</span>
                              <span className="text-sm font-bold text-blue-600">+2 points</span>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-indigo-100">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-700">Automation Email Open</span>
                              <span className="text-sm font-bold text-amber-600">+10 points</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    
                    {/* A. Summary Section */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">📊 Summary Statistics</h3>
                      <div className="grid grid-cols-4 gap-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="text-2xl font-bold text-blue-700">{activityData.summary.totalSends}</div>
                          <div className="text-xs font-medium text-blue-600 mt-1">Total Sent</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="text-2xl font-bold text-blue-700">{activityData.summary.automationSends}</div>
                          <div className="text-xs font-medium text-blue-600 mt-1">Automation Sent</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="text-2xl font-bold text-blue-700">{activityData.summary.totalOpens}</div>
                          <div className="text-xs font-medium text-blue-600 mt-1">Total Opens</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="text-2xl font-bold text-blue-700">{activityData.summary.automationOpens}</div>
                          <div className="text-xs font-medium text-blue-600 mt-1">Automation Opens</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="text-2xl font-bold text-blue-700">{activityData.summary.totalReplies}</div>
                          <div className="text-xs font-medium text-blue-600 mt-1">Total Replies</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="text-2xl font-bold text-blue-700">{activityData.summary.currentStep}/{activityData.summary.maxSteps}</div>
                          <div className="text-xs font-medium text-blue-600 mt-1">Current Step</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="text-2xl font-bold text-blue-700">{activityData.summary.leadScore}</div>
                          <div className="text-xs font-medium text-blue-600 mt-1">Lead Score</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* B. Step Progress Bar (1-5) - Only show if lead has entered automation */}
                    {activityData.summary.hasEnteredAutomation && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-bold text-gray-900 flex items-center space-x-2">
                          <span>🎯</span>
                          <span>Step Progress</span>
                        </h3>
                        <button
                          onClick={() => setShowColorHint(!showColorHint)}
                          className="p-1 hover:bg-blue-50 rounded-md transition-colors"
                          title="Color Guide"
                        >
                          <Info className="w-3.5 h-3.5 text-blue-600" />
                        </button>
                      </div>
                      
                      {/* Color Hint Popup */}
                      {showColorHint && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-2.5 mb-2 shadow-sm"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <h4 className="text-xs font-bold text-gray-800 flex items-center">
                              <Info className="w-3 h-3 mr-1 text-blue-600" />
                              Color Guide
                            </h4>
                            <button
                              onClick={() => setShowColorHint(false)}
                              className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                            <div className="flex items-center space-x-1.5">
                              <div className="w-3 h-3 bg-green-500 rounded border border-green-600"></div>
                              <span className="text-gray-700">Opened</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                              <div className="w-3 h-3 bg-yellow-500 rounded border border-yellow-600"></div>
                              <span className="text-gray-700">Sent only</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                              <div className="w-3 h-3 bg-blue-500 rounded border border-blue-600"></div>
                              <span className="text-gray-700">Current</span>
                            </div>
                            <div className="flex items-center space-x-1.5">
                              <div className="w-3 h-3 bg-white rounded border border-gray-300"></div>
                              <span className="text-gray-700">Future</span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                      <div className="grid grid-cols-5 gap-1.5">
                        {activityData.stepProgress.map((step) => {
                          // Determine color based on status
                          let bgColor, borderColor, textColor, shadowClass;
                          
                          if (step.hasOpens) {
                            // Opened - Green
                            bgColor = 'bg-green-500';
                            borderColor = 'border-green-600';
                            textColor = 'text-white';
                            shadowClass = 'shadow-sm';
                          } else if (step.hasSent) {
                            // Sent but NOT opened - Yellow
                            bgColor = 'bg-yellow-500';
                            borderColor = 'border-yellow-600';
                            textColor = 'text-gray-900';
                            shadowClass = 'shadow-sm';
                          } else if (step.isCurrent) {
                            // Current NEXT pending step - Blue
                            bgColor = 'bg-blue-500';
                            borderColor = 'border-blue-600';
                            textColor = 'text-white';
                            shadowClass = 'shadow-md ring-2 ring-blue-300 ring-opacity-50';
                          } else {
                            // Future pending steps - White
                            bgColor = 'bg-white';
                            borderColor = 'border-gray-300';
                            textColor = 'text-gray-700';
                            shadowClass = '';
                          }
                          
                          return (
                            <div
                              key={step.step}
                              className={`${bgColor} ${borderColor} ${textColor} ${shadowClass} border rounded-lg p-2.5 text-center transition-all duration-200 hover:scale-105`}
                            >
                              <div className="flex items-center justify-center space-x-0.5 mb-1">
                                <span className="text-lg font-bold">{step.step}</span>
                                {step.hasOpens && (
                                  <CheckCircle className="w-3.5 h-3.5" />
                                )}
                              </div>
                              <div className="space-y-0.5">
                                {step.sentCount > 0 && (
                                  <div className="text-[10px] font-medium opacity-90">
                                    Sent: {step.sentCount}
                                  </div>
                                )}
                                <div className="text-[10px] font-medium">
                                  {step.openCount} {step.openCount === 1 ? 'open' : 'opens'}
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-wide mt-1 opacity-80">
                                  {step.status}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    )}
                    
                    {/* D. Real-Time Activity Summary Table */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">📅 Activity Summary</h3>
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden max-h-96">
                        <div className="overflow-y-auto max-h-96">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Campaign</th>
                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Step</th>
                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Score</th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Timestamp</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {activityData.activityTable.map((activity, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 text-sm">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      activity.type === 'Open' ? 'bg-green-100 text-green-700' :
                                      activity.type === 'Reply' ? 'bg-purple-100 text-purple-700' :
                                      activity.type === 'Sent' ? 'bg-blue-100 text-blue-700' :
                                      activity.type === 'Click' ? 'bg-orange-100 text-orange-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {activity.type}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700 font-medium">{activity.campaign}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600 text-center">{activity.step}</td>
                                  <td className="px-4 py-3 text-center">
                                    <span className={`text-sm font-bold ${
                                      activity.score.toString().startsWith('+') ? 'text-green-600' : 'text-gray-600'
                                    }`}>
                                      {activity.score}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    {new Date(activity.timestamp).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No activity data available</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LeadDetail;